import type {
  DashboardContentSubject,
  DashboardContentTip,
  DashboardContentWeek,
} from '../dal/contentful-dashboard-dal'
import type { ProgressRecord } from '../dal/progress-dal'
import type {
  DashboardExerciseCard,
  DashboardExerciseType,
} from '../types/dashboard'
import { buildContentfulImageUrl } from '../utils/contentful-image'
import { getYearWeekWeeksAgo } from '../utils/year-week'

const clampWeek = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max)
}

export const resolveContentWeekFromActiveWeek = (
  activeWeek: number,
  totalWeeks: number,
) => {
  const normalizedTotalWeeks = Math.max(1, totalWeeks)
  const normalizedActiveWeek = Math.max(1, activeWeek)

  return ((normalizedActiveWeek - 1) % normalizedTotalWeeks) + 1
}

export const resolveDashboardAccessibleWeekCount = ({
  activeWeek,
  programWeekCount,
}: {
  activeWeek: number
  programWeekCount: number
}) => {
  const normalizedProgramWeekCount = Math.max(1, programWeekCount)

  return clampWeek(activeWeek, 1, normalizedProgramWeekCount)
}

export const resolveProgramWeekCount = (
  weeks: DashboardContentWeek[],
): number => {
  return weeks.reduce((maxWeek, week) => {
    const weekNumber = week.week
    return weekNumber != null && weekNumber > maxWeek ? weekNumber : maxWeek
  }, 1)
}

const buildProgressKey = (weekId: string, sessionId: string): string => {
  return `${weekId}::${sessionId}`
}

export const buildProgressCountMap = (
  progressItems: ProgressRecord[],
): Map<string, number> => {
  const counts = new Map<string, number>()

  progressItems.forEach((progressItem) => {
    const weekId = progressItem.contentfulWeekId
    const sessionId = progressItem.contentfulSessionId

    if (weekId == null || sessionId == null) return

    const key = buildProgressKey(weekId, sessionId)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  })

  return counts
}

const buildSessionCards = ({
  subject,
  week,
  exerciseType,
  progressCountMap,
}: {
  subject: DashboardContentSubject
  week: DashboardContentWeek
  exerciseType: DashboardExerciseType
  progressCountMap: Map<string, number>
}): DashboardExerciseCard[] => {
  const weekId = week.id
  if (typeof weekId !== 'string' || weekId.length === 0) return []

  const subjectId = subject.id
  const subjectTitle = subject.title ?? ''
  if (subject.programId == null) {
    console.warn('Program is not set for subject', subject.id)
    return []
  }
  const programId = subject.programId

  const groupedSessions = new Map<
    string,
    {
      session: DashboardContentWeek['sessions'][number]
      targetRepetitions: number
    }
  >()

  const weekSessions = week.sessions
  if (Array.isArray(weekSessions)) {
    weekSessions.forEach((sessionItem) => {
      if (sessionItem == null) return
      const existing = groupedSessions.get(sessionItem.id)
      if (existing != null) {
        existing.targetRepetitions += 1
        return
      }

      groupedSessions.set(sessionItem.id, {
        session: sessionItem,
        targetRepetitions: 1,
      })
    })
  }

  const cards: DashboardExerciseCard[] = []

  for (const [sessionId, groupedSession] of groupedSessions.entries()) {
    const session = groupedSession.session
    const targetRepetitions = groupedSession.targetRepetitions
    const completedRepetitions =
      progressCountMap.get(buildProgressKey(weekId, sessionId)) ?? 0
    const sessionTitle = session.title ?? ''

    cards.push({
      id: `${subjectId}:${weekId}:${sessionId}`,
      exerciseType,
      sessionId,
      weekId,
      subjectId,
      programId,
      subjectTitle,
      title: sessionTitle.length ? sessionTitle : subjectTitle,
      lengthMinutes: session.lengthMinutes,
      iconUrl: buildContentfulImageUrl(
        subject.icon?.url,
        subject.icon?.contentType ?? '',
        'icon',
      ),
      hasPreview: session.media != null || session.description != null,
      targetRepetitions,
      completedRepetitions,
      isCompleted: completedRepetitions >= targetRepetitions,
    })
  }

  return cards
}

export const resolveMorningRoutineWeek = (
  subject: DashboardContentSubject | null,
  activeWeek: number,
): DashboardContentWeek | null => {
  if (subject == null) return null

  return (
    subject.weeks.find((week) => week.week === activeWeek) ??
    subject.weeks[0] ??
    null
  )
}

export const resolveMondayFirstWeekdayIndex = (date: Date): number => {
  const dayIndex = date.getDay()
  return dayIndex === 0 ? 6 : dayIndex - 1
}

export const resolveDailyWarmupExercise = ({
  exercises,
  date,
}: {
  exercises: DashboardExerciseCard[]
  date: Date
}): DashboardExerciseCard | null => {
  if (exercises.length >= 7) {
    return exercises[resolveMondayFirstWeekdayIndex(date)] ?? null
  }

  return exercises[0] ?? null
}

export const resolveDailyTip = ({
  tips,
  date,
}: {
  tips: DashboardContentTip[]
  date: Date
}): DashboardContentTip | null => {
  // JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat. Contentful may use same or ISO (1=Mon, 7=Sun).
  const todayJs = date.getDay()
  const todayIso = todayJs === 0 ? 7 : todayJs // Sun=7, Mon=1, ..., Sat=6

  return (
    tips.find(
      (tip) => tip.day != null && (tip.day === todayJs || tip.day === todayIso),
    ) ??
    tips.find((tip) => tip.tip?.trim()) ??
    null
  )
}

export const buildDailyWarmupExercise = ({
  morningSubject,
  activeWeek,
  progressCountMap,
  date,
}: {
  morningSubject: DashboardContentSubject | null
  activeWeek: number
  progressCountMap: Map<string, number>
  date: Date
}): DashboardExerciseCard | null => {
  const week = resolveMorningRoutineWeek(morningSubject, activeWeek)

  const dailyWarmupCandidates =
    morningSubject && week
      ? buildSessionCards({
          subject: morningSubject,
          week,
          exerciseType: 'daily',
          progressCountMap,
        })
      : []

  return resolveDailyWarmupExercise({
    exercises: dailyWarmupCandidates,
    date,
  })
}

export const buildWeeklyExercises = ({
  subjects,
  activeWeek,
  progressCountMap,
}: {
  subjects: DashboardContentSubject[]
  activeWeek: number
  progressCountMap: Map<string, number>
}): DashboardExerciseCard[] => {
  const weeklyExercises: DashboardExerciseCard[] = []

  subjects.forEach((subject) => {
    const activeWeekEntry = subject.weeks.find(
      (week) => week.week === activeWeek,
    )

    if (activeWeekEntry == null) {
      console.warn('Active week entry not found for subject', subject.id)
      return
    }

    // Some historical week entries do not have the legacy `dailyActivities` flag.
    // Treat missing as weekly so those sessions are still rendered.
    const exerciseType =
      activeWeekEntry.dailyActivities === true ? 'daily' : 'weekly'

    const cards = buildSessionCards({
      subject,
      week: activeWeekEntry,
      exerciseType,
      progressCountMap,
    })

    if (cards.length === 0) {
      return
    }

    if (exerciseType === 'weekly') {
      weeklyExercises.push(...cards)
    }
  })

  return weeklyExercises
}

export const resolveDashboardWeeks = ({
  currentProgramWeek,
  selectedWeek,
  weeks,
}: {
  currentProgramWeek: number
  selectedWeek: number
  weeks: DashboardContentWeek[]
}) => {
  const programWeekCount = resolveProgramWeekCount(weeks)
  const normalizedCurrentProgramWeek = clampWeek(
    currentProgramWeek,
    1,
    programWeekCount,
  )
  const selectedProgramWeek = clampWeek(selectedWeek, 1, programWeekCount)
  const weeksAgo =
    selectedProgramWeek <= normalizedCurrentProgramWeek
      ? normalizedCurrentProgramWeek - selectedProgramWeek
      : normalizedCurrentProgramWeek + (programWeekCount - selectedProgramWeek)

  return {
    currentProgramWeek: normalizedCurrentProgramWeek,
    programWeekCount,
    selectedProgramWeek,
    weeksAgo,
    yearWeek: getYearWeekWeeksAgo({ weeksAgo }),
  }
}
